import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import { usePopup } from '@/composables/usePopup'
import { useStrings } from '@/composables/useStrings'
import { useAppStore } from '@/stores/useAppStore'
import { useCubeStore } from '@/stores/useCubeStore'
import { assetPath } from '@/util'
import strings from '@/util/Strings'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useNotificationsStore = defineStore('notifications', () => {
  const { logError } = useLogError()
  const { backendApi } = useHttpClient()
  const { showPopup } = usePopup()
  const { t } = useStrings()

  const notificationSound = ref<HTMLAudioElement | null>(null)

  // --- Pure helper functions ---

  function browserSupportsNotifications(): boolean {
    return 'Notification' in window
  }

  function hasNotificationsPermission(): boolean {
    if (!browserSupportsNotifications()) {
      return false
    }
    return Notification.permission === 'granted'
  }

  async function getBrowserWebPushSubscription(): Promise<PushSubscription | false> {
    const swRegistration = useAppStore().serviceWorkerRegistration
    if (!swRegistration) {
      throw new Error('Serviceworker not registered yet.')
    }
    if (!('pushManager' in swRegistration)) {
      return false
    }

    return await swRegistration.pushManager.getSubscription() || false
  }

  function showNotification(data: NotificationOptions) {
    if (notificationSound.value) {
      notificationSound.value.play()
    }

    return new Notification('eFriends', { icon: assetPath('favicons/favicon.ico'), ... data})
  }

  // --- Actions ---

  async function setupPushNotifications() {
    const cubeStore = useCubeStore()
    if (!browserSupportsNotifications()) {
      return
    }

    const webPushSubscription = await getBrowserWebPushSubscription()
    const hasPermission = hasNotificationsPermission()
    const userHasSubscribed = cubeStore.webPushValue > 0 && cubeStore.notificationsValue > 0

    if (userHasSubscribed && hasPermission && webPushSubscription) {
      return
    }

    if (userHasSubscribed) {
      // The user enabled subscription in another instance but something is not set up here.
      if (hasPermission) {
        subscribeToPushApi()
      }
    } else if (cubeStore.webPushValue === 0 || cubeStore.notificationsValue === 0) {
      // TODO APP-569: Show notificationsRequest popup once popup system is migrated.

      showPopup('notificationsRequest')
    }
  }

  async function requestNotificationsPermission() {
    try {
      notificationSound.value = new Audio('/notification.mp3')
      if (Notification.permission === 'default' || Notification.permission === 'granted') {
        const permission = await Notification.requestPermission()
        await notificationsPermissionRequested(permission)
      }
    } catch (e) {
      // Safari on iOS still uses the old callback-based API.
      Notification.requestPermission((permission) => {
        notificationsPermissionRequested(permission)
      })
    }
  }

  async function notificationsPermissionRequested(permission: string) {
    const cubeStore = useCubeStore()

    // Older Chrome versions need the permission stored manually.
    if (!('permission' in Notification)) {
      ;(Notification as any).permission = permission
    }

    if (permission !== 'granted') {
      return
    }

    try {
      const result = await backendApi.post('/app/notifications/subscribe', {})

      if (
        !result ||
        result.status.toString().substring(0, 1) !== '2' ||
        !result.data.bSuccess
      ) {
        throw new Error(
          `Error updating notification subscription on server: ${result.data.Message}.`
        )
      }

      cubeStore.notificationsValue = 1
      cubeStore.webPushValue = 1
      showNotification({
        body: strings.notifications.welcomeNotification
      })
    } catch (e) {
      logError('Error requesting notifications permission: ' + e)
    }
  }

  async function subscribeToPushApi() {
    const appStore = useAppStore()
    const cubeStore = useCubeStore()

    try {
      const swRegistration = appStore.serviceWorkerRegistration
      if (!swRegistration) {
        throw new Error('Serviceworker not registered yet.')
      }

      let subscription: PushSubscription | null | false = null

      try {
        subscription = await getBrowserWebPushSubscription()
        // TODO APP-569: Handle case where subscription is an empty object.
        if (
          !subscription ||
          (Object.keys(subscription).length === 0 && subscription.constructor === Object)
        ) {
          subscription = await swRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY,
          })
        }
      } catch (error) {
        console.error('Could not push subscribe', error)
      }

      // The subscription object only exposes all data when stringified.
      // Send a dummy subscription if subscribing failed, to allow server cleanup.
      const subscriptionData = !!subscription
        ? JSON.parse(JSON.stringify(subscription))
        : { endpoint: '', expirationTime: null, keys: { p256dh: '', auth: '' } }

      const result = await backendApi.post('/app/push/subscribe', subscriptionData)

      if (
        !result ||
        result.status.toString().substring(0, 1) !== '2' ||
        !result.data.bSuccess
      ) {
        throw new Error(
          `Error updating push subscription on server: ${result.data.Message}.`
        )
      }

      cubeStore.webPushValue = subscription ? 1 : -1
    } catch (err) {
      throw new Error('Error adding push subscription to notification service: ' + err)
    } finally {
      // Original code had dispatch("enableNotifications", true) here, which was a bug
      // (no such action exists — only a mutation). Applying the intended effect directly.
      cubeStore.notificationsValue = 1
      cubeStore.webPushValue = cubeStore.webPushValue > 0 ? cubeStore.webPushValue : 1
    }
  }

  async function renewSubscription() {
    const appStore = useAppStore()
    const cubeStore = useCubeStore()

    const swRegistration = appStore.serviceWorkerRegistration
    if (!swRegistration) {
      throw new Error('Serviceworker not registered yet.')
    }

    let subscription = await getBrowserWebPushSubscription()
    // TODO APP-569: Handle case where subscription is an empty object.
    if (
      !subscription ||
      (Object.keys(subscription).length === 0 && subscription.constructor === Object)
    ) {
      subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY,
      })
    }

    const subscriptionData = JSON.parse(JSON.stringify(subscription))

    const result = await backendApi.post('/app/push/subscribe', subscriptionData)

    if (
      !result ||
      result.status.toString().substring(0, 1) !== '2' ||
      !result.data.bSuccess
    ) {
      throw new Error(
        `Error updating push subscription on server: ${result.data.Message}.`
      )
    }

    cubeStore.webPushValue = subscription ? 1 : -1
  }

  async function unsubscribeFromPushApi() {
    const cubeStore = useCubeStore()

    try {
      const result = await backendApi.post('/app/push/unsubscribe', {})

      if (
        !result ||
        result.status.toString().substring(0, 1) !== '2' ||
        !result.data.bSuccess
      ) {
        throw new Error(
          `Error unsubscribing from push on server: ${result.data.Message}.`
        )
      }

      cubeStore.webPushValue = -1

      // TODO really unsubscribe in the browser too?

      // const subscription = await getBrowserWebPushSubscription();

      // if (subscription) {
      //   const success = await subscription.unsubscribe();

      //   if (success) {
      //       console.log("Push subscription removed");
      //   }
      // }
    } catch (err) {
      throw new Error('Error removing push subscription to notification service: ' + err)
    }
  }

  async function disableNotifications() {
    const cubeStore = useCubeStore()

    try {
      const result = await backendApi.post('/app/notifications/unsubscribe', {})

      if (
        !result ||
        result.status.toString().substring(0, 1) !== '2' ||
        !result.data.bSuccess
      ) {
        throw new Error(
          `Error disabling notification subscription on server: ${result.data.Message}.`
        )
      }

      cubeStore.notificationsValue = -1
    } catch (e) {
      logError('Error disabling notifications: ' + e)
    }
  }

  async function subscribeToEmailNotifications() {
    const cubeStore = useCubeStore()

    try {
      const result = await backendApi.post('/app/email/subscribe', { cubeName: cubeStore.name })

      if (!result || result.status.toString().substring(0, 1) !== '2' || !result.data.bSuccess) {
        const errorMessage = t("errors." + result.data.errorCode, null, "Unbekannter Fehler");
        showNotification({ body: t('global.emailNotificationsError', { '@error': errorMessage }) })

        throw new Error(`Error enabling email subscription on server: ${t("errors." + result.data.errorCode)}.`)
      }

      cubeStore.emailSubscription = true
    } catch (e) {
      logError('Error enabling email subscription: ' + e)
    }
  }

  async function unsubscribeFromEmailNotifications() {
    const cubeStore = useCubeStore()

    try {
      const result = await backendApi.post('/app/email/unsubscribe', { cubeName: cubeStore.name })

      if (
        !result ||
        result.status.toString().substring(0, 1) !== '2' ||
        !result.data.bSuccess
      ) {
        throw new Error(
          `Error unsubscribing email subscription on server: ${result.data.Message}.`
        )
      }

      cubeStore.emailSubscription = false
    } catch (e) {
      logError('Error unsubscribing email subscription: ' + e)
    }
  }

  return {
    notificationSound,
    browserSupportsNotifications,
    hasNotificationsPermission,
    setupPushNotifications,
    requestNotificationsPermission,
    subscribeToPushApi,
    renewSubscription,
    unsubscribeFromPushApi,
    disableNotifications,
    subscribeToEmailNotifications,
    unsubscribeFromEmailNotifications,
    showNotification
  }
})
